import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { cn } from '../lib/cn';

/**
 * Dialog — Modal centrado con overlay.
 *
 * Uso recomendado (props directas):
 *   <Dialog
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     title="Crear usuario"
 *     description="Datos básicos del nuevo perfil"
 *     footer={
 *       <>
 *         <Button variant="secondary" onClick={() => setOpen(false)}>Cancelar</Button>
 *         <Button onClick={guardar}>Guardar</Button>
 *       </>
 *     }
 *   >
 *     Contenido del modal
 *   </Dialog>
 *
 * El cuerpo (`children`) lleva padding propio (px-5 py-5) y, si se
 * pasa `footer`, se renderiza una barra inferior con `border-t` y
 * fondo suave. Los sub-componentes `DialogContent` y `DialogFooter`
 * siguen exportados por retrocompatibilidad con código viejo.
 */
export const Dialog = ({
    open,
    onClose,
    title,
    description,
    footer,
    size = 'md',
    children,
    className,
}) => {
    // Cerrar con ESC
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (e.key === 'Escape') onClose?.();
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [open, onClose]);

    // Bloquear scroll del body cuando está abierto
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, [open]);

    if (!open) return null;

    const SIZES = {
        sm: 'max-w-md',
        md: 'max-w-lg',
        lg: 'max-w-2xl',
        xl: 'max-w-4xl',
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className={cn(
                    'w-full bg-white rounded-xl shadow-lg border border-[hsl(var(--border))]',
                    'flex flex-col max-h-[90vh]',
                    SIZES[size],
                    className
                )}
            >
                {/* Header */}
                {(title || onClose) && (
                    <div className="flex items-start justify-between px-5 py-4 border-b border-[hsl(var(--border))] shrink-0">
                        <div className="flex-1 min-w-0">
                            {title && (
                                <h2 className="text-base font-semibold text-slate-900">{title}</h2>
                            )}
                            {description && (
                                <p className="text-sm text-slate-500 mt-1">{description}</p>
                            )}
                        </div>
                        {onClose && (
                            <button
                                type="button"
                                onClick={onClose}
                                aria-label="Cerrar"
                                className="ml-3 h-7 w-7 flex items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                            >
                                <X className="size-4" />
                            </button>
                        )}
                    </div>
                )}

                {/* Body — envoltorio con padding propio (antes children iba pelado
                    y se pegaba al header). Si no hay children, no se renderiza
                    para que un modal solo de "header + footer" (ej. una
                    confirmación) no muestre un hueco vacío. */}
                {children && (
                    <div className="px-5 py-5 overflow-y-auto flex-1 min-h-0">
                        {children}
                    </div>
                )}

                {/* Footer — opt-in vía prop `footer`. Tira inferior con
                    border-top y fondo suave, igual que un footer de Card.
                    Si no se pasa, no se renderiza. */}
                {footer && (
                    <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[hsl(var(--border))] bg-slate-50 shrink-0">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
};

export const DialogContent = ({ className, ...props }) => (
    <div className={cn('px-5 py-4 overflow-y-auto', className)} {...props} />
);

export const DialogFooter = ({ className, ...props }) => (
    <div
        className={cn(
            'flex items-center justify-end gap-2 px-5 py-3 border-t border-[hsl(var(--border))]',
            className
        )}
        {...props}
    />
);
